import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import { FiMail, FiSave, FiEye, FiEyeOff, FiCheck, FiAlertCircle, FiZap } from 'react-icons/fi';

const EmailConfigAdmin = () => {
  const [config, setConfig] = useState({
    smtp_host: 'api.mailgun.net',
    smtp_port: 443,
    smtp_user: 'api',
    smtp_pass: '',
    sender_email: 'updates@hopp.sg',
    mailgun_domain: 'mg.hopp.sg',
    mailgun_api_key: 'cb926e8151838ea2656b48ac2b6ca6cd-03fd4b1a-b7b4fa36'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [configId, setConfigId] = useState(null);

  useEffect(() => {
    loadExistingConfig();
  }, []);

  const loadExistingConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('email_config')
        .select('*')
        .limit(1);

      if (error) {
        console.error('Error loading config:', error);
        return;
      }

      if (data && data.length > 0) {
        const existingConfig = data[0];
        setConfig({
          smtp_host: existingConfig.smtp_host || 'api.mailgun.net',
          smtp_port: existingConfig.smtp_port || 443,
          smtp_user: existingConfig.smtp_user || 'api',
          smtp_pass: '••••••••', // Don't show actual password
          sender_email: existingConfig.sender_email || 'updates@hopp.sg',
          mailgun_domain: existingConfig.mailgun_domain || 'mg.hopp.sg',
          mailgun_api_key: existingConfig.mailgun_api_key || 'cb926e8151838ea2656b48ac2b6ca6cd-03fd4b1a-b7b4fa36'
        });
        setConfigId(existingConfig.id);
        console.log("Loaded Mailgun config ID:", existingConfig.id);
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      // Prepare the config data
      const configData = {
        smtp_host: config.smtp_host,
        smtp_port: parseInt(config.smtp_port),
        smtp_user: config.smtp_user,
        sender_email: config.sender_email,
        mailgun_domain: config.mailgun_domain,
        mailgun_api_key: config.mailgun_api_key,
        updated_at: new Date().toISOString()
      };

      // Only include password if it's been changed
      if (config.smtp_pass && config.smtp_pass !== '••••••••') {
        configData.smtp_pass = config.smtp_pass;
      }

      console.log("Saving Mailgun config with ID:", configId);
      console.log("Config data:", configData);

      let result;
      if (configId) {
        // Update existing config
        result = await supabase
          .from('email_config')
          .update(configData)
          .eq('id', configId)
          .select();
      } else {
        // Insert new config
        result = await supabase
          .from('email_config')
          .insert([configData])
          .select();
      }

      if (result.error) {
        console.error("Error saving Mailgun config:", result.error);
        throw result.error;
      }

      console.log("Save result:", result);

      // Set the config ID if we just created a new record
      if (result.data && result.data.length > 0 && !configId) {
        setConfigId(result.data[0].id);
      }

      setMessage('✅ Mailgun API Konfiguration erfolgreich gespeichert!');

      // Reset password field to show dots if it was changed
      if (config.smtp_pass && config.smtp_pass !== '••••••••') {
        setConfig(prev => ({ ...prev, smtp_pass: '••••••••' }));
      }
    } catch (error) {
      console.error('Failed to save Mailgun config:', error);
      setMessage('❌ Fehler beim Speichern: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-xl shadow-lg">
      <div className="flex items-center mb-6">
        <SafeIcon icon={FiZap} className="text-2xl text-purple-500 mr-3" />
        <h2 className="text-2xl font-bold text-gray-800">Mailgun API Konfiguration</h2>
      </div>

      <div className="space-y-4">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="font-semibold text-purple-800 mb-2">🚀 Mailgun API Einstellungen</h3>
          <p className="text-sm text-purple-700">
            Mailgun ist ein professioneller E-Mail-Service mit hoher Zuverlässigkeit und Deliverability.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mailgun Domain
          </label>
          <input
            type="text"
            value={config.mailgun_domain}
            onChange={(e) => handleInputChange('mailgun_domain', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="mg.hopp.sg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mailgun API Key
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={config.mailgun_api_key}
              onChange={(e) => handleInputChange('mailgun_api_key', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Mailgun API Key eingeben"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            API Host (Standard)
          </label>
          <input
            type="text"
            value={config.smtp_host}
            onChange={(e) => handleInputChange('smtp_host', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="api.mailgun.net"
            disabled
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            API Port (Standard)
          </label>
          <input
            type="number"
            value={config.smtp_port}
            onChange={(e) => handleInputChange('smtp_port', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="443"
            disabled
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Absender E-Mail
          </label>
          <input
            type="email"
            value={config.sender_email}
            onChange={(e) => handleInputChange('sender_email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="updates@hopp.sg"
          />
        </div>

        {message && (
          <div className={`p-3 rounded-lg flex items-center ${
            message.includes('✅') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            <SafeIcon icon={message.includes('✅') ? FiCheck : FiAlertCircle} className="mr-2" />
            {message}
          </div>
        )}

        <div className="pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <SafeIcon icon={FiSave} className="mr-2" />
            {saving ? 'Wird gespeichert...' : 'Mailgun Konfiguration speichern'}
          </button>
        </div>
      </div>

      <div className="mt-6 p-4 bg-purple-50 rounded-lg">
        <h3 className="font-semibold text-purple-800 mb-2">🚀 Mailgun Konfiguration:</h3>
        <ul className="text-sm text-purple-700 space-y-1">
          <li>• <strong>Domain:</strong> mg.hopp.sg</li>
          <li>• <strong>API Key:</strong> cb926e8151838ea2656b48ac2b6ca6cd-03fd4b1a-b7b4fa36</li>
          <li>• <strong>Key ID:</strong> 03fd4b1a-b7b4fa36</li>
          <li>• <strong>API Host:</strong> api.mailgun.net</li>
          <li>• <strong>Zuverlässigkeit:</strong> 99.9% Uptime</li>
          <li>• <strong>Rate Limit:</strong> 10.000 E-Mails/Monat kostenlos</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailConfigAdmin;